import { LanguagesEnum } from '@/api/enums';
import { DEFAULT_ENROLLMENT_HOUR } from '@/common/constants';
import { learnerOnlyLanguages } from '@/common/constants/languages';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users';
import { isThereTranslation } from '@/utils/helpers';
import { t } from 'i18next';
import moment from 'moment';
import { CalendarBaseProps } from 'primereact/calendar';
import React from 'react';
import styled from 'styled-components';
import AppCalendar from './AppCalendar';
import { TimeControls } from './TimeControls';

const StyledCalendar = styled(AppCalendar)`
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  > input {
    height: auto;
    border-radius: var(--xsmall-border-radius);
    padding: var(--small-padding);
  }

  &:hover {
    border-color: var(--red-main);
  }
`;

const RadioToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 21px;
  flex: 0 0 100%;
  padding: 4px 0 10px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
`;

const RadioDot = styled.div<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 9px;
  border: ${({ $selected }) =>
    $selected ? '5px solid var(--red-main)' : '1px solid var(--gray-dark)'};
  background: var(--white-main);
  flex-shrink: 0;
  box-sizing: border-box;
`;

const RadioLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
`;

const EnrollmentFooter = styled.div`
  border-top: 1px solid var(--gray-dark);
  margin-top: var(--small-padding);
  padding: var(--small-padding) var(--medium-padding);
`;

const EnrollNowDescription = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  max-width: 280px;
  margin: 0;
`;

type AppCalendarWithEnrollmentTimePickerProps = Omit<
  CalendarBaseProps,
  'onChange' | 'value'
> & {
  value?: Date | null;
  enrollmentHour?: number;
  enrollImmediately?: boolean;
  onDateChange?: (date: Date) => void;
  onEnrollmentHourChange?: (hour: number) => void;
  onEnrollImmediatelyChange?: (immediately: boolean) => void;
  className?: string;
};

export const AppCalendarWithEnrollmentTimePicker: React.FC<
  AppCalendarWithEnrollmentTimePickerProps
> = ({
  value,
  enrollmentHour = DEFAULT_ENROLLMENT_HOUR,
  enrollImmediately = false,
  onDateChange,
  onEnrollmentHourChange,
  onEnrollImmediatelyChange,
  ...rest
}) => {
  const user = useAppSelector(selectCurrentUser);

  const isToday = value ? moment(value).isSame(moment(), 'day') : false;

  const handleDateChange = (e: { value: any }) => {
    const newDate = e.value ? (e.value as Date) : undefined;
    if (!newDate) return;

    onDateChange?.(newDate);

    const newIsToday = moment(newDate).isSame(moment(), 'day');

    if (enrollImmediately && !newIsToday) {
      onEnrollImmediatelyChange?.(false);
    }

    if (!enrollImmediately && newIsToday) {
      const currentHour = new Date().getHours();
      if (enrollmentHour <= currentHour) {
        const nextHour = currentHour + 1;
        if (nextHour < 24) {
          onEnrollmentHourChange?.(nextHour);
        } else {
          onEnrollImmediatelyChange?.(true);
        }
      }
    }
  };

  const handleTimeChange = (newValue: Date | null) => {
    if (!newValue) return;
    onEnrollmentHourChange?.(newValue.getHours());
  };

  const switchToEnrollNow = () => {
    onEnrollImmediatelyChange?.(true);
    if (!isToday) {
      onDateChange?.(new Date());
    }
  };

  const switchToChooseTime = () => {
    onEnrollImmediatelyChange?.(false);
    if (isToday) {
      const nextHour = new Date().getHours() + 1;
      if (nextHour < 24) {
        onEnrollmentHourChange?.(nextHour);
      }
    }
  };

  const timeValue = React.useMemo(() => {
    if (!value || enrollImmediately) return null;
    const d = new Date(value);
    d.setHours(enrollmentHour);
    d.setMinutes(0);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
  }, [value, enrollmentHour, enrollImmediately]);

  const formatDisplayValue = (date: Date): string => {
    const dateStr = moment(date).format('DD MMM YY');
    if (enrollImmediately) {
      return `${dateStr} • ${moment(date).format('HH:mm')}`;
    }
    return `${dateStr} • ${String(enrollmentHour).padStart(2, '0')}:00`;
  };

  const headerContent = () => (
    <RadioToggleRow>
      <RadioOption onClick={switchToChooseTime}>
        <RadioDot $selected={!enrollImmediately} />
        <RadioLabel>{t('planner.enrollment.choose_time')}</RadioLabel>
      </RadioOption>
      <RadioOption onClick={switchToEnrollNow}>
        <RadioDot $selected={enrollImmediately} />
        <RadioLabel>{t('planner.enrollment.enroll_immediately')}</RadioLabel>
      </RadioOption>
    </RadioToggleRow>
  );

  const footerContent = () => (
    <EnrollmentFooter>
      {enrollImmediately ? (
        <EnrollNowDescription>
          {t('planner.enrollment.enroll_now_description')}
        </EnrollNowDescription>
      ) : (
        <TimeControls
          value={timeValue}
          onChange={handleTimeChange}
          minDate={isToday ? new Date() : rest?.minDate}
          hoursOnly
        />
      )}
    </EnrollmentFooter>
  );

  return (
    <StyledCalendar
      panelClassName={`enrollment-calendar${
        enrollImmediately ? ' enroll-now-mode' : ''
      }`}
      value={value}
      onChange={handleDateChange}
      locale={
        isThereTranslation() &&
        !learnerOnlyLanguages.includes(user?.language as LanguagesEnum)
          ? user?.language
          : LanguagesEnum.EN
      }
      formatDateTime={formatDisplayValue}
      footerTemplate={footerContent}
      headerTemplate={headerContent}
      selectionMode="single"
      {...rest}
    />
  );
};
